/**
 * Repo: PWT.PlattformUI
 * Component: <SearchBlock>
 *
 * @description
 *  Complete search page block component
 *  with form, field and results list.
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'qs';
import React, { Fragment } from 'react';
import MatomoTrackSearch from '../../platformui/Matomo/matomo-track-search';
import Alert from '../Alert';
import CollapsiblePanel from '../CollapsiblePanel';
import withSearch from '../Containers/withSearch';
import Facets from '../Facets';
import KeywordHints from '../KeywordHints';
import Pagination from '../Pagination';
import SearchField from '../Search/searchfield';
import Spinner from '../Spinner';
import TabMenu from '../TabMenu';
import { getExternalSiteAdress } from './external-search-utils';
import List from './list';
import SearchHeading from './searchheading';

const Search = (props) => {
  const qs = queryString.parse(props.location.search, {
    ignoreQueryPrefix: true,
  });
  const classes = classNames({
    'is-loading': props.SearchResult.isLoading,
    searchblock: true,
  });

  const searchToolsClasses = classNames({
    'c-search__tools': true,
    'c-search__tools--has-facets': props?.Facets,
  });

  const alertType = props.alertType ? props.alertType : 'warning';

  let activeFilterFacet = '';

  if (props.showFilterResultText) {
    props.Facets?.Items?.map((item) => {
      item.Facets.some((item) => {
        if (item.Id === qs.facet && item.Id !== 'all') {
          activeFilterFacet = item.Text;
        }
      });
    });
  }

  return (
    <div className={classes}>
      <form
        className="c-search"
        method="get"
        autoComplete="off"
        data-cy="ui-searchform"
        onSubmit={(e) => {
          props.onSubmit(e, {
            q: document.getElementById('section-search').value,
            t: qs.t,
            ...(props.useExternalSearch && { site: qs.site }),
          });
        }}
      >
        <div className="c-search__field">
          <label
            className="c-search__field__label icon-search"
            htmlFor="section-search"
          >
            {props.Placeholder ? (
              <span className="show-for-sr">{props.Placeholder}</span>
            ) : null}
          </label>

          <SearchField
            id="section-search"
            className="c-search"
            searchUrl=""
            placeholder={props.Placeholder}
            defaultValue={qs.q}
            hasQuickSearch={props.hasQuickSearch}
            hasNoAutocomplete={props.hasNoAutocomplete}
            SearchApiAutoCompleteUrl={props.SearchApiAutoCompleteUrl}
            showSearchHitsHeadingSuffix={props.showSearchHitsHeadingSuffix}
            defaultQuickSearch={props.CurrentPageProps.QuickSearchSettings}
            site={props.CurrentPageProps.Site}
          />
          <input type="hidden" name="t" value={qs.t} />
          <button
            className="c-search__field__button"
            type="submit"
            data-cy="ui-searchform-submit-btn"
          >
            {props.Translations.searchbutton}
          </button>
        </div>
        {props.SearchResult.KeywordMessage && (
          <KeywordHints KeywordMessage={props.SearchResult.KeywordMessage} />
        )}

        {props.SearchResult.IsSimpleFallbackSearch && (
          <Alert type={alertType} alert={props}>
            {props.SearchResult.ResultDisclaimer}
          </Alert>
        )}

        {props.searchTabs && props.ExternalSearchContent && (
          <TabMenu
            key={props.searchApiIndex}
            tabs={props.searchTabs?.map((tab) => ({
              key: tab.SearchApiIndex,
              label: tab.TabHeader,
              count: tab.SearchResult?.TotalHits || 0,
              isActive: tab.SearchApiIndex === props.searchApiIndex,
              screenReaderText: `${tab.SearchResult?.TotalHits} ${
                tab.SearchResult?.ResultText
              } ${getExternalSiteAdress(tab.SearchApiIndex)}`,
            }))}
            handleTabChange={props.handleTabChange}
            showCount
            showResponsiveDropdown
            srLabel={props?.Translations?.selectsearchsource}
          />
        )}

        {props.Facets &&
          props.CurrentPageProps.hasFilterBar &&
          !props.showFilterBarWithFacets && (
            <Facets
              {...props}
              queryString={qs}
              pathname={props.location.pathname}
            />
          )}

        {qs.q && (
          <Fragment>
            <div className={searchToolsClasses}>
              <SearchHeading
                {...props.SearchResult}
                query={qs.q}
                activeFilterFacet={activeFilterFacet}
                showFilterResultText={props.showFilterResultText}
                within={props.Translations.within}
                translations={props.Translations}
                isVardpersonal1177={props.vardpersonal1177}
                siteAdress={!!qs?.site && getExternalSiteAdress(qs?.site)}
              />
              {((props.showFilterBarWithFacets && props.Facets) ||
                ((props.HideCategoryFacets === false ||
                  props.HideThemeFacets === false) &&
                  !props.CurrentPageProps.hasFilterBar)) && (
                <CollapsiblePanel
                  Id="facetspanel"
                  className="c-collapsible-facets"
                  DefaultCollapsed={
                    qs.category || qs.theme || !props?.filterBarDefaultOpen
                  }
                  Heading={props.Translations.filtersearchhitsheading}
                >
                  <Facets
                    {...props}
                    queryString={qs}
                    pathname={props.location.pathname}
                  />
                </CollapsiblePanel>
              )}
            </div>

            {qs.q.length > 0 &&
              props.SearchResult.TotalHits === 0 &&
              props.SearchResult.EmptySearchResultText && (
                <Alert type={alertType} alert={props}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: props.SearchResult.EmptySearchResultText,
                    }}
                  ></div>
                </Alert>
              )}
          </Fragment>
        )}
      </form>
      <List
        {...props.SearchResult}
        DisableClickTracking={props.DisableClickTracking}
        translations={props.Translations}
        query={qs.q}
        revisedAsPublished={props.revisedAsPublished}
        vardpersonal1177={props.vardpersonal1177}
        isExternalSearchHit={props?.isExternalSearch}
        regionalizeSearchHits={props?.searchApiIndex === '1177'}
        availableLanguagesHeader={props?.AvailableLanguagesHeader}
      >
        <Pagination
          onClick={() => document.getElementById('search-list-last').focus()}
          {...props.SearchResult}
          Spinner={<Spinner Modifiers={{ 'c-spinner--search': true }} />}
        >
          {props.SearchResult.BatchText}
        </Pagination>
      </List>
      <MatomoTrackSearch
        Search={qs?.q}
        SearchCategory={props.searchApiIndex || 'Globalt sök'}
        SearchHits={props.SearchResult?.TotalHits}
        ExternalSearchHits={props.searchTabs}
      />
    </div>
  );
};

Search.propTypes = {
  //**Used when any website wants the revised text to be published text */
  revisedAsPublished: PropTypes.bool,
  location: PropTypes.object,
  /** When we use custom click tracking - disable it with `true`. */
  DisableClickTracking: PropTypes.bool,
  /** isLoading is used by autocomplete. See search reducers */
  showFilterResultText: PropTypes.bool,
  SearchResult: PropTypes.shape({
    isLoading: PropTypes.bool,
    TotalHits: PropTypes.number,
    EmptySearchResultText: PropTypes.string,
    BatchText: PropTypes.string,
  }),
  /** From withSearch class */
  onSubmit: PropTypes.func.isRequired,
  Translations: PropTypes.shape({
    searchprevioussr: PropTypes.string,
    searchbutton: PropTypes.string,
    filtersearchhitsheading: PropTypes.string,
  }),
  Placeholder: PropTypes.string,
  HideCategoryFacets: PropTypes.bool,
  HideThemeFacets: PropTypes.bool,
  vardpersonal1177: PropTypes.bool,
  showFilterBarWithFacets: PropTypes.bool,
  useExternalSearch: PropTypes.bool,
  showSearchHitsHeadingSuffix: PropTypes.bool,
  filterBarDefaultOpen: PropTypes.bool,
  searchOnMount: PropTypes.bool,
  isExternalSearch: PropTypes.bool,
  availableLanguagesHeader: PropTypes.string,
};

export default withSearch(Search, 'Search', null, {});

export { Search };
